import i18n from '@/utils/i18n';
import React, { useCallback, useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  entryToItemListOption,
  MinimalScannerVariantPackListItem,
} from './minimal-scanner-variant-pack-list-item';
import {
  KeyboardAwareRecyclerList,
  ListSection,
} from '@/components/recycler-list';
import { ListOption } from '@/components/list-item';
import { ScannerType } from './scanner-types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { UpdateQuantityData } from '@/contexts/new-order-actions-context-types';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useLocalScannerContext } from '@/contexts/local-scanner-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { VariantPackEntry } from '@/adapters/offline-mode';
import { View } from 'react-native';

type MinimalListOption = ListOption<VariantPackEntry>;
interface SectionMetadata {
  empty: boolean;
  type: 'orderGuide' | 'catalog';
}

// the estimate the recycler list uses before it measures real row heights;
// bump it when the additional info row adds a line to every row
const ESTIMATED_ITEM_SIZE = 80;
const ESTIMATED_ITEM_SIZE_WITH_ADDITIONAL_INFO = 110;

// KeyboardAwareRecyclerList wants ListOptions
function entriesToListOptions(
  entries: VariantPackEntry[],
): MinimalListOption[] {
  return entries.map((entry) => ({
    metadata: entry,
    title: entry.display_name,
    value: entry.uuid,
  }));
}

interface Props {
  scannerType: ScannerType;
}

export const MinimalScanResults: React.FC<Props> = ({ scannerType }) => {
  const [styles] = useStyles();
  const { selectItemListOption } = useItemDetailContext();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { searchResults, setMultiUpdates, codeRef } = useLocalScannerContext();
  const additionalProductInfoEnabled = useSupplierDynamicConfigFlag(
    'on_device_scanning_config',
    'additional_product_info_enabled',
  );

  const sections = useMemo<
    ListSection<MinimalListOption, SectionMetadata>[]
  >(() => {
    if (!searchResults) {
      return [];
    }

    const ogTitle = i18n.t('scan.order_guide_results');
    const catalogTitle = i18n.t('scan.catalog_results');

    const { orderGuideResults, catalogResults } = searchResults;

    const ogSection = {
      data:
        orderGuideResults.length > 0
          ? entriesToListOptions(orderGuideResults)
          : [],
      key: ogTitle,
      metadata: {
        empty: orderGuideResults.length === 0,
        type: 'orderGuide' as const,
      },
    };

    const catalogSection = {
      data:
        catalogResults.length > 0 ? entriesToListOptions(catalogResults) : [],
      key: catalogTitle,
      metadata: {
        empty: catalogResults.length === 0,
        type: 'catalog' as const,
      },
    };

    return [ogSection, catalogSection];
  }, [searchResults]);

  const handleUpdateQuantity = useCallback(
    (entry: VariantPackEntry, updateQuantityData: UpdateQuantityData) => {
      setMultiUpdates((prev) => ({
        ...prev,
        [updateQuantityData.variantPackUUID]: {
          entry,
          quantity: updateQuantityData.newQuantity,
        },
      }));
    },
    [setMultiUpdates],
  );

  return (
    <View
      style={[
        styles.flex,
        scannerType === ScannerType.External ? styles.listMargin : null,
      ]}
    >
      <KeyboardAwareRecyclerList
        estimatedItemSize={
          additionalProductInfoEnabled
            ? ESTIMATED_ITEM_SIZE_WITH_ADDITIONAL_INFO
            : ESTIMATED_ITEM_SIZE
        }
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps="handled"
        listItemProps={{
          listItemStyle: styles.listItem,
          onSelect: (item) => {
            if (!item.metadata) {
              return;
            }
            selectItemListOption(entryToItemListOption(item.metadata), {
              source: scannerType,
            });
          },
          renderMainContent: (option) => (
            <MinimalScannerVariantPackListItem
              additionalProductInfoEnabled={additionalProductInfoEnabled}
              debouncedUpdateQuantity={debouncedUpdateQuantity}
              entry={option.metadata!}
              matchedCode={searchResults?.matchedCodes[option.metadata!.uuid]}
              onUpdateQuantity={handleUpdateQuantity}
              source="scanner"
            />
          ),
        }}
        renderSectionHeader={(item) => {
          if (item.metadata?.empty) {
            const isOrderGuide = item.metadata?.type === 'orderGuide';
            return (
              <View style={styles.emptySectionHeader}>
                <Typography variant="paragraph-sm">
                  {isOrderGuide
                    ? i18n.t('order_guide_search_header.empty_state', {
                        searchTerm: codeRef.current,
                      })
                    : i18n.t('no_results_from_catalog_search_term', {
                        searchTerm: codeRef.current,
                      })}
                </Typography>
              </View>
            );
          }

          return (
            <View style={styles.sectionHeaderContainer}>
              <Typography variant="heading-sm">{item.title}</Typography>
            </View>
          );
        }}
        sections={sections}
        shouldRenderSeparator={() => false}
        showSeparator={false}
        stickyHeaderIndices={[]}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  emptySectionHeader: {
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  flex: {
    flex: 1,
  },
  listItem: {
    backgroundColor: 'transparent',
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  listMargin: {
    marginVertical: theme.sk.spacing.spaceLarge,
  },
  sectionHeaderContainer: {
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
}));
